import { AUTOMATION_CENTER_TIME_REDUCTION_CAP, AUTOMATION_CENTER_TIME_REDUCTION_PER_LEVEL, BUILDING_DEFINITIONS, RECYCLING_INCOME_BASE_PERCENT, RECYCLING_INCOME_MAX_PERCENT, SHIPYARD_TIME_REDUCTION_CAP, SHIPYARD_TIME_REDUCTION_PER_LEVEL, SHIP_UPGRADE_COST_GROWTH, SHIP_UPGRADE_EFFECT, SHIP_UPGRADE_MAX_LEVEL, SHIP_UPGRADE_PERCENT_PER_LEVEL, SHIP_UPGRADE_REFUND_MAX, SHIP_UPGRADE_REFUND_MIN, SHIP_UPGRADE_TIME_GROWTH, TECH_CENTER_MIN_LEVEL_FOR_UPGRADES, TECH_CENTER_TIME_REDUCTION_CAP, TECH_CENTER_TIME_REDUCTION_LATE_BASE, TECH_CENTER_TIME_REDUCTION_PER_LEVEL_EARLY, TECH_CENTER_TIME_REDUCTION_PER_LEVEL_LATE, UPGRADABLE_SHIP_IDS, getShipDefinition, getShipUpgradeBase, } from "@kaeliux/shared";
import { meetsRequirements } from "./buildings.js";
/**
 * Construction time for a batch of ships (`docs/game-details/ships.md`,
 * "Ship Production Times"):
 *
 *   per_unit = (base_time / speed)
 *              × (1 − min(shipyard_level × 5%, 75%))
 *              × (1 − min(automation_center_level × 5%, 25%))
 *
 * The result is floored to whole seconds (min 1) per unit, so a batch's
 * total is always an exact multiple of its per-unit time — cancellation
 * prorates finished units from it.
 */
export function calculateShipBuildTime(race, shipId, quantity, shipyardLevel, automationCenterLevel, speedMultiplier) {
    const def = getShipDefinition(race, shipId);
    const speed = speedMultiplier > 0 ? speedMultiplier : 1;
    const shipyardReduction = Math.min(SHIPYARD_TIME_REDUCTION_CAP, SHIPYARD_TIME_REDUCTION_PER_LEVEL * Math.max(0, shipyardLevel));
    const automationReduction = Math.min(AUTOMATION_CENTER_TIME_REDUCTION_CAP, AUTOMATION_CENTER_TIME_REDUCTION_PER_LEVEL * Math.max(0, automationCenterLevel));
    const perUnitSeconds = Math.max(1, Math.floor((def.buildTimeSeconds / speed) * (1 - shipyardReduction) * (1 - automationReduction)));
    return {
        perUnitSeconds,
        totalSeconds: perUnitSeconds * Math.max(0, quantity),
    };
}
/**
 * Which shipyard FIFO (`ShipBuildLane`) a ship's build orders chain behind
 * on a given planet (issue #91). A pure, race-keyed lookup — mirrors the
 * `SHIP_DEFINITIONS` convention of branching on race via data, not
 * `if (race === ...)` chains:
 *
 * - Humans and Robots: always `"default"` — a single shipyard line, same as
 *   every race's behavior before this feature. Robots' distinguishing
 *   mechanic is the delivery-delay group system (`planShipBuildGroupQueueEntry`),
 *   not a lane split.
 * - Axons: `"mutation"` for ships with `mutatesFrom` set, `"creation"`
 *   otherwise — so a mutation build (which already consumes docked donor
 *   ships) never blocks an unrelated regular build queued behind it.
 */
const SHIP_BUILD_LANES = {
    humans: () => "default",
    robots: () => "default",
    axons: (def) => (def.mutatesFrom ? "mutation" : "creation"),
};
export function computeShipBuildLane(race, def) {
    return SHIP_BUILD_LANES[race](def);
}
/** Total metal/minerals/gas for `quantity` units. Zero-cost resources are omitted. */
export function calculateShipBuildCost(race, shipId, quantity) {
    const def = getShipDefinition(race, shipId);
    const cost = {};
    for (const key of ["metal", "minerals", "gas"]) {
        const base = def.cost[key];
        if (base)
            cost[key] = base * quantity;
    }
    return cost;
}
// ---------------------------------------------------------------------------
// Recovery Center — scrap → ships (issue #21). Queue staging / cancellation
// / replay application live in `tickEvents.ts`, mirroring the shipyard.
// ---------------------------------------------------------------------------
/**
 * Recovery Center conversion rate at `plantLevel` (1-indexed), as a whole
 * percent: `RECYCLING_INCOME_BASE_PERCENT` at L1, rising linearly to
 * `RECYCLING_INCOME_MAX_PERCENT` at the building's max level
 * (`BUILDING_DEFINITIONS.recyclingPlant.maxLevel`, currently 10). Levels
 * below 1 have no conversion; levels above max level clamp to the max rate.
 */
export function calculateRecyclingIncomePercent(plantLevel) {
    if (plantLevel < 1)
        return 0;
    const maxLevel = BUILDING_DEFINITIONS.recyclingPlant.maxLevel;
    if (maxLevel <= 1)
        return RECYCLING_INCOME_MAX_PERCENT;
    const progress = Math.min(1, (plantLevel - 1) / (maxLevel - 1));
    return Math.round(RECYCLING_INCOME_BASE_PERCENT + progress * (RECYCLING_INCOME_MAX_PERCENT - RECYCLING_INCOME_BASE_PERCENT));
}
/**
 * Scrap cost to recycle `shipSelection` into ships at `plantLevel`:
 *
 *   baseCost   = sum of (metal + minerals + gas) for every selected ship
 *   scrapCost  = ceil( baseCost × 100 / incomePercent(plantLevel) )
 *
 * i.e. a ship's build cost IS its scrap cost at 100% income; a lower-level
 * plant (lower income%) costs proportionally more scrap for the same ships
 * (docs: "if a ship costs 1200 metal and 800 mineral, it will cost 2000
 * scrap at max building level... at level 1 it will be 100/50 × scrap_required").
 * Returns 0 for an unbuilt plant (`plantLevel < 1`) or an empty selection.
 */
export function recycleScrapRequired(race, shipSelection, plantLevel) {
    const incomePercent = calculateRecyclingIncomePercent(plantLevel);
    if (incomePercent <= 0)
        return 0;
    let baseCost = 0;
    for (const [key, quantity] of Object.entries(shipSelection)) {
        if (!quantity)
            continue;
        const cost = calculateShipBuildCost(race, Number(key), quantity);
        baseCost += (cost.metal ?? 0) + (cost.minerals ?? 0) + (cost.gas ?? 0);
    }
    if (baseCost <= 0)
        return 0;
    return Math.ceil((baseCost * 100) / incomePercent);
}
/** Whether the ship's Factory / science prerequisites are met (actual levels only). */
export function checkShipRequirements(race, shipId, buildingLevels, scienceLevels = {}) {
    const def = getShipDefinition(race, shipId);
    return meetsRequirements(def.requirements, buildingLevels, scienceLevels);
}
/**
 * Total ship count across every ship type — unweighted, unlike
 * `calculateDockedPopulation`. Named distinctly from `battle.ts`'s
 * `totalShipCount` (which sums a `BattleUnitGroup[]` mid-fight, a different
 * shape) — this one sums a plain `PlanetShips`-style record.
 */
export function totalShips(ships) {
    if (!ships)
        return 0;
    return Object.values(ships).reduce((sum, n) => sum + (n ?? 0), 0);
}
/** Population slots occupied by docked ships (hangar-weighted). */
export function calculateDockedPopulation(race, ships) {
    if (!ships)
        return 0;
    let used = 0;
    for (const [key, count] of Object.entries(ships)) {
        if (!count)
            continue;
        used += getShipDefinition(race, Number(key)).hangar * count;
    }
    return used;
}
function isPendingShipBuild(e) {
    return e.eventType === "shipBuild" && !e.cancelled;
}
/** Population slots reserved by pending shipBuild queue entries. */
export function calculateQueuedShipPopulation(race, pending) {
    return pending.filter(isPendingShipBuild).reduce((used, e) => used + getShipDefinition(race, e.eventDetails.shipType).hangar * e.eventDetails.quantity, 0);
}
function isPendingScrapRecycle(e) {
    return e.eventType === "scrapRecycle" && !e.cancelled;
}
/**
 * Population slots reserved by pending Recovery Center batches. A batch
 * docks its ships on completion exactly like a shipBuild entry does, so it
 * has to hold their hangar slots from queue time (issue #119).
 */
export function calculateQueuedRecyclePopulation(race, pending) {
    return pending.filter(isPendingScrapRecycle).reduce((used, e) => used + calculateDockedPopulation(race, e.eventDetails.ships), 0);
}
/**
 * Population slots a planet currently has spoken for: docked ships, ships
 * away on a flight from this planet (`shipsInFlight` — a fleet in transit
 * still occupies its hangar slots until it is destroyed or transferred,
 * issue #294), plus every pending batch that will dock ships — shipBuild
 * AND scrapRecycle. This is the number the UI's Population tile reports,
 * and the single definition of "used population" — callers must not re-add
 * the terms themselves, or the pages drift apart (issue #89).
 *
 * `pending` must be a planet's whole `tickCheckpoints` array; cancelled and
 * non-docking events are ignored, but a list pre-filtered to one event type
 * silently under-reports the other.
 */
export function calculateUsedPopulation(race, ships, pending, shipsInFlight) {
    return (calculateDockedPopulation(race, ships) +
        calculateDockedPopulation(race, shipsInFlight) +
        calculateQueuedShipPopulation(race, pending) +
        calculateQueuedRecyclePopulation(race, pending));
}
/**
 * Free population = Launcher-derived capacity minus used population (see
 * {@link calculateUsedPopulation}) — sending ships out never frees room to
 * build replacements. Ship construction is bounded by this, never by a
 * queue-slot limit.
 *
 * As with {@link calculateUsedPopulation}, `pending` must be the planet's
 * whole `tickCheckpoints` array for the number to be complete.
 */
export function calculateFreePopulation(race, populationCapacity, ships, pending, shipsInFlight) {
    return populationCapacity - calculateUsedPopulation(race, ships, pending, shipsInFlight);
}
/**
 * A planet is overpopulated when its DOCKED ships occupy more population
 * than the Launcher-derived capacity allows — a deliberate mechanic (issue
 * #26): players can intentionally overpopulate a planet by sending multiple
 * simultaneous Station flights at once, e.g. to concentrate defense or
 * quickly clear solar satellites. Ships in flight or still queued for
 * construction never count — only what's actually docked right now,
 * mirroring {@link calculateDockedPopulation}.
 */
export function isPlanetOverpopulated(race, planet) {
    return calculateDockedPopulation(race, planet.ships) > planet.resources.population.capacity;
}
// ---------------------------------------------------------------------------
// Ship upgrades (Tech Center / Stargate / Space Channel) — see
// `docs/game-details/ship-upgrades.md`. Queue staging / cancellation /
// replay application live in `tickEvents.ts`, mirroring buildings/science.
// ---------------------------------------------------------------------------
/** Whether `shipId` can be upgraded at all (transports, war ships, and the recycling ship — never colony/spy/satellite). */
export function isShipUpgradable(shipId) {
    return UPGRADABLE_SHIP_IDS.includes(shipId);
}
/** Which stat(s) `shipId`'s upgrade improves, or `undefined` if it isn't upgradable. */
export function getShipUpgradeEffect(shipId) {
    return SHIP_UPGRADE_EFFECT[shipId];
}
/**
 * Cost to upgrade `race`'s variant of `shipId` TO `level` (level >= 1):
 *
 *   cost(level) = base.cost × 2^(level - 1)
 *
 * where `base` is the ship's level-1 upgrade cost (`getShipUpgradeBase`).
 */
export function calculateShipUpgradeCost(race, shipId, level) {
    const base = getShipUpgradeBase(race, shipId);
    if (!base)
        throw new Error(`Ship ${shipId} (${race}) is not upgradable`);
    if (level < 1)
        throw new Error(`Ship upgrade level must be >= 1, got ${level}`);
    const factor = Math.pow(SHIP_UPGRADE_COST_GROWTH, level - 1);
    const cost = {};
    for (const key of ["metal", "minerals", "gas"]) {
        const amount = base.cost[key];
        if (amount)
            cost[key] = Math.round(amount * factor);
    }
    return cost;
}
/**
 * Fraction the Tech Center (`techCenter`) reduces ship-upgrade time by, per
 * `docs/game-details/ship-upgrades.md` "Upgrade-Time Reduction Building
 * Mechanic": 5%/level through level 5, then `25% + 10%/level` above that,
 * capped at 75% (level 10).
 */
export function calculateTechCenterUpgradeTimeReduction(techCenterLevel) {
    const level = Math.max(0, techCenterLevel);
    const reduction = level <= 5
        ? level * TECH_CENTER_TIME_REDUCTION_PER_LEVEL_EARLY
        : TECH_CENTER_TIME_REDUCTION_LATE_BASE + (level - 5) * TECH_CENTER_TIME_REDUCTION_PER_LEVEL_LATE;
    return Math.min(TECH_CENTER_TIME_REDUCTION_CAP, reduction);
}
/**
 * Upgrade duration for `race`'s variant of `shipId` TO `level` (level >= 1), in seconds.
 *
 *   time(level) = base.timeSeconds × 1.4^(level - 1)
 *   result       = floor( time(level) × (1 − techCenterReduction) / speedMultiplier )
 */
export function calculateShipUpgradeTime(race, shipId, level, techCenterLevel, speedMultiplier = 1) {
    const base = getShipUpgradeBase(race, shipId);
    if (!base)
        throw new Error(`Ship ${shipId} (${race}) is not upgradable`);
    if (level < 1)
        throw new Error(`Ship upgrade level must be >= 1, got ${level}`);
    const rawTime = base.timeSeconds * Math.pow(SHIP_UPGRADE_TIME_GROWTH, level - 1);
    const reduction = calculateTechCenterUpgradeTimeReduction(techCenterLevel);
    const speed = speedMultiplier > 0 ? speedMultiplier : 1;
    return Math.max(1, Math.floor((rawTime * (1 - reduction)) / speed));
}
/**
 * Ship stats after applying `level` upgrade levels. Only the stat(s)
 * `getShipUpgradeEffect` names for this ship change; the rest pass through
 * unchanged. A non-upgradable ship (no effect) or level 0 returns the base
 * stats as-is. Battle resolution and the UI must read stats through this
 * (never `def.attack`/`def.life`/`def.cargo`/`def.speed` directly) once a
 * ship has any upgrade level.
 */
export function getUpgradedShipStats(def, level) {
    const effect = getShipUpgradeEffect(def.id);
    const percent = SHIP_UPGRADE_PERCENT_PER_LEVEL[def.id] ?? 0;
    const multiplier = 1 + Math.max(0, level) * percent;
    return {
        attack: effect === "attackLife" ? Math.round(def.attack * multiplier) : def.attack,
        life: effect === "attackLife" ? Math.round(def.life * multiplier) : def.life,
        cargo: effect === "cargo" ? Math.round(def.cargo * multiplier) : def.cargo,
        speed: effect === "speed" ? Math.round(def.speed * multiplier) : def.speed,
    };
}
/**
 * Whether a ship's upgrade can be queued: max level, the Tech Center gate ("not
 * available without the building at minimum level 1"), and — per issue #23 —
 * the SAME prerequisites required to build the ship in the first place
 * (`checkShipRequirements`, actual levels only).
 */
export function canUpgradeShip(race, shipId, currentLevel, buildingLevels, scienceLevels = {}) {
    if (!isShipUpgradable(shipId))
        return false;
    if (currentLevel >= SHIP_UPGRADE_MAX_LEVEL)
        return false;
    if ((buildingLevels.techCenter ?? 0) < TECH_CENTER_MIN_LEVEL_FOR_UPGRADES)
        return false;
    return checkShipRequirements(race, shipId, buildingLevels, scienceLevels);
}
/** Refund rate for a cancelled ship upgrade, from an injected 0..1 roll — "Cancel refunds 60–80% of resources". */
export function calculateShipUpgradeRefundRate(refundRoll) {
    const clamped = Math.min(1, Math.max(0, refundRoll));
    return SHIP_UPGRADE_REFUND_MIN + clamped * (SHIP_UPGRADE_REFUND_MAX - SHIP_UPGRADE_REFUND_MIN);
}
//# sourceMappingURL=ships.js.map